import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { BrandMark } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Wrench, ListChecks, ArrowRight, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function SolutionDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get(`/solutions/${slug}`).then(({ data }) => {
      setS(data);
      document.title = `${data.title} — CLING`;
      const meta = document.querySelector('meta[name="description"]') || document.head.appendChild(Object.assign(document.createElement("meta"), { name: "description" }));
      meta.setAttribute("content", (data.problem || "").slice(0, 160));
      const og = document.querySelector('meta[property="og:title"]') || document.head.appendChild(Object.assign(document.createElement("meta"), {}));
      og.setAttribute("property", "og:title"); og.setAttribute("content", data.title);
    }).catch(() => setS({ notFound: true }));
    return () => { document.title = "CLING"; };
  }, [slug]);

  if (!s) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;
  if (s.notFound) return <div className="min-h-screen grid place-items-center text-muted-foreground">Solution not found.</div>;

  const goGetHelp = () => nav(`/signup?intent=help&context=${encodeURIComponent(s.title)}&category=${encodeURIComponent(s.category || "")}`);
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: s.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    } catch {}
  };

  return (
    <div className="min-h-screen warm-grain">
      <header className="border-b bg-background/85 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={share} data-testid="solution-share"><Share2 className="w-3 h-3 mr-1" /> Share</Button>
            <Button size="sm" className="btn-teal" onClick={goGetHelp} data-testid="solution-get-help">Get Help</Button>
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">{s.category} · Public solution</div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl">{s.title}</h1>
        <p className="mt-4 text-muted-foreground">{s.problem}</p>

        {s.warnings?.length > 0 && (
          <div className="mt-6 border-l-4 border-[color:hsl(var(--amber))] bg-[color:hsl(var(--amber))]/10 rounded-r-xl p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-[color:hsl(var(--amber))]"><AlertTriangle className="w-4 h-4" /> Warnings</div>
            <ul className="mt-2 text-sm space-y-1 list-disc pl-5">{s.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
          </div>
        )}

        <section className="mt-6 border rounded-2xl bg-card p-5">
          <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><ListChecks className="w-4 h-4" /> Steps</div>
          <ol className="mt-3 space-y-2 list-decimal pl-5 text-sm">{s.steps?.map((st, i) => <li key={i}>{st}</li>)}</ol>
        </section>

        {s.tools?.length > 0 && (
          <section className="mt-4 border rounded-2xl bg-card p-5">
            <div className="flex items-center gap-2 text-sm font-mono-label text-[color:hsl(var(--teal))]"><Wrench className="w-4 h-4" /> Tools</div>
            <div className="mt-3 flex flex-wrap gap-2">{s.tools.map((t) => <span key={t} className="text-xs rounded-full bg-secondary px-3 py-1 border">{t}</span>)}</div>
          </section>
        )}

        <section className="mt-4 border rounded-2xl bg-card p-5">
          <div className="text-sm font-mono-label text-[color:hsl(var(--amber))]">Solution</div>
          <p className="mt-2 text-sm whitespace-pre-wrap">{s.solution}</p>
        </section>

        {s.helper_attribution?.length > 0 && (
          <section className="mt-6">
            <div className="text-xs font-mono-label text-muted-foreground">Helped by</div>
            <div className="mt-3 flex flex-wrap gap-2">
              {s.helper_attribution.map((h) => (
                <span key={h.user_id} className="border rounded-full px-3 py-1 text-xs">{h.name} · {h.people_helped || 0} helped</span>
              ))}
            </div>
          </section>
        )}

        <section className="mt-10 border-2 border-[color:hsl(var(--teal))]/40 bg-[color:hsl(var(--teal))]/5 rounded-3xl p-6 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="text-xs font-mono-label text-[color:hsl(var(--teal))]">Still stuck?</div>
            <div className="mt-1 font-display font-bold text-xl">Your situation may be different.</div>
            <div className="text-sm text-muted-foreground mt-1">Get immediate AI help, then connect with someone who's fixed this before.</div>
          </div>
          <Button className="btn-teal rounded-full h-11 px-5" onClick={goGetHelp} data-testid="solution-cta">Get help with this <ArrowRight className="w-4 h-4 ml-1" /></Button>
        </section>

        <footer className="mt-10 text-xs text-muted-foreground">
          People + AI. Better together. Public solution — private conversations remain private. <button onClick={share} className="underline underline-offset-2 inline-flex items-center gap-1 ml-2"><Copy className="w-3 h-3" /> Copy link</button>
        </footer>
      </article>
    </div>
  );
}
